<template>
  <PopupContent :bodyClass="['paywall-body']">
    <template #header>
      <div class="paywall-header">{{ $t('paywall.title') }}</div>
    </template>

    <div class="paywall-message">{{ localize(viewModel.message) }}</div>

    <div v-if="statusMessage" class="paywall-status success">{{ statusMessage }}</div>
    <div v-if="errorMessage" class="paywall-status error">{{ errorMessage }}</div>
    <ReferralShareBanner compact />

    <template #footer>
      <div class="paywall-actions">
        <button class="paywall-cancel utility-button-font" type="button" @click="cancel">
          {{ $t('common.cancel') }}
        </button>
        <button class="paywall-button utility-button-font" type="button" @click="openMembership" :disabled="isPurchasing">
          {{ isPurchasing ? $t('subscription.processing') : $t('paywall.getMembership') }}
        </button>
      </div>
    </template>
  </PopupContent>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { PremiumTier, type PaywallPopupViewModel } from '@lingvo-news/contracts';
import PopupContent from './PopupContent.vue';
import { localize } from '../../utils/localize';
import { PremiumPaymentClient } from '../../infrastructure/services/premium-payment-client';
import ReferralShareBanner from './ReferralShareBanner.vue';

interface Props {
  readonly viewModel: PaywallPopupViewModel;
}

const props = defineProps<Props>();

const isPurchasing = ref(false);
const statusMessage = ref<string | null>(null);
const errorMessage = ref<string | null>(null);

type InvoiceStatus = 'paid' | 'failed' | 'cancelled' | 'pending';

async function openTelegramInvoice(): Promise<void> {
  isPurchasing.value = true;
  statusMessage.value = null;
  errorMessage.value = null;

  try {
    const client = new PremiumPaymentClient();
    const invoice = await client.createTelegramInvoice(PremiumTier.TelegramMonthly);
    const status = await openInvoiceLink(invoice.invoiceSlug);

    if (status === 'paid') {
      statusMessage.value = 'Payment received. Activating premium…';
    } else if (status === 'pending') {
      statusMessage.value = 'Invoice opened. Complete the payment in Telegram.';
    } else if (status === 'cancelled') {
      errorMessage.value = 'Payment was cancelled.';
    } else if (status === 'failed') {
      errorMessage.value = 'Payment failed. Please try again.';
    }
  } catch {
    errorMessage.value = 'Failed to start payment. Please try again.';
  } finally {
    isPurchasing.value = false;
  }
}

async function openInvoiceLink(invoiceSlug: string): Promise<InvoiceStatus> {
  const telegram = (window as typeof window & {
    Telegram?: { WebApp?: { openInvoice?: (slug: string, callback: (status: InvoiceStatus) => void) => boolean } }
  }).Telegram;
  const openInvoice = telegram?.WebApp?.openInvoice;

  if (!openInvoice) {
    window.open(invoiceSlug, '_blank', 'noopener');
    return 'pending';
  }

  return await new Promise<InvoiceStatus>((resolve, reject) => {
    const opened = openInvoice(invoiceSlug, (status: InvoiceStatus) => {
      resolve(status ?? 'pending');
    });
    if (!opened) {
      reject(new Error('Failed to open Telegram invoice.'));
    }
  });
}

function openMembership(): void {
  if (props.viewModel.useTelegramPayment) {
    void openTelegramInvoice();
    return;
  }
  window.open(props.viewModel.buyMeCoffeeUrl, '_blank', 'noopener,noreferrer');
}

function cancel(): void {
  props.viewModel.cancel();
}
</script>

<style scoped>
.paywall-body {
  padding: 0;
}

.paywall-header {
  font-size: var(--font-size-header);

  color: #111;
}

.paywall-message {
  font-size: var(--font-size-button);
  color: #333;
  line-height: 1.5;
  margin-bottom: 18px;
}

.paywall-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.paywall-cancel {
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 10px 16px;

  cursor: pointer;
  background: #fff;
  color: #333;
  font-family: inherit;
}

.paywall-cancel:hover {
  background: rgba(0, 0, 0, 0.03);
}

.paywall-button {
  border: none;
  border-radius: 10px;
  padding: 10px 16px;

  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: inherit;
}

.paywall-button:hover {
  filter: brightness(1.05);
}

.paywall-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.paywall-status {
  font-size: var(--font-size-button);
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.paywall-status.success {
  background: #efe;
  color: #2a7a2a;
}

.paywall-status.error {
  background: #fee;
  color: #c33;
}
</style>

